// src/pages/NotificationsPage/NotificationsPage.jsx
import React, { useEffect, useRef } from 'react';
import { auth, db } from '../../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { notificationsStyles } from './styles';
import { useNotifications } from './useNotifications';
import NotificationsList from './NotificationsList';

// Bell Icon SVG
const BellIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/**
 * NotificationsPage
 * Shows meeting invites and lets seller accept, reject, or suggest new time/date with a message.
 */
const NotificationsPage = () => {
  const user = auth.currentUser;
  const hasMarkedAsRead = useRef(false);
  
  const {
    notifications,
    setNotifications,
    loading,
    senders,
    expanded,
    setExpanded,
    respondingId,
    setRespondingId,
    justSuggestedId,
    setJustSuggestedId,
    response,
    setResponse
  } = useNotifications(user);

  // Track notification page visit and mark unread notifications as read
  useEffect(() => {
    if (!user) return;

    // Always update localStorage when user visits notifications page
    localStorage.setItem(`lastNotificationsVisit_${user.uid}`, new Date().toISOString());

    // Mark unread notifications as read (one-time operation)
    if (loading || notifications.length === 0 || hasMarkedAsRead.current) return;

    const markNotificationsAsRead = async () => {
      for (const notification of notifications) {
        // Only mark 'unread' as 'read', leave 'pending' as 'pending' (still awaiting action)
        // But we'll track localStorage visit to clear notification dot
        if (notification.status === 'unread') {
          try {
            const notifRef = doc(db, 'notifications', notification.id);
            await updateDoc(notifRef, {
              status: 'read'
            });
          } catch (error) {
          }
        }
      }
      // Mark that we've done the initial read marking
      hasMarkedAsRead.current = true;
    };

    markNotificationsAsRead();
  }, [user, loading, notifications]);

  return (
    <div style={notificationsStyles.pageContainer}>
      <div style={notificationsStyles.container}>
        {/* Header */}
        <div style={notificationsStyles.header}>
          <div style={notificationsStyles.headerIcon}>
            <BellIcon />
          </div>
          <div>
            <h1 style={notificationsStyles.title}>Notifications</h1>
            <p style={notificationsStyles.subtitle}>
              {loading 
                ? 'Loading notifications...'
                : notifications.length > 0 
                  ? `You have ${notifications.length} notification${notifications.length !== 1 ? 's' : ''}`
                  : 'All caught up!'}
            </p>
          </div>
        </div>
        
        <NotificationsList
          notifications={notifications}
          setNotifications={setNotifications}
          loading={loading}
          senders={senders}
          expanded={expanded}
          setExpanded={setExpanded}
          user={user}
          respondingId={respondingId}
          setRespondingId={setRespondingId}
          response={response}
          setResponse={setResponse}
          justSuggestedId={justSuggestedId}
          setJustSuggestedId={setJustSuggestedId}
        />
      </div>
    </div>
  );
};

export default NotificationsPage;